import * as config from './config.json'
import { AuthenticationData, APIResponse, Selector, LogicalOperators, QueryObject } from './types'
import { BitBucketAPI, APIEndpoint, LocalStorageKey, Query, PartialResponse, Tag } from './const'
import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios'
import {
  RedirectRequestHandler,
  AuthorizationServiceConfiguration,
  AuthorizationRequest,
  GRANT_TYPE_AUTHORIZATION_CODE
} from '@openid/appauth'

//helper
let auth: AuthenticationData

function getLocalStorageItem(toRetrieve: string) {
  const requestId = localStorage.getItem(LocalStorageKey.requestId)
  if (requestId) {
    switch (toRetrieve) {
      case LocalStorageKey.requestId:
        return requestId
      case LocalStorageKey.authRequest:
        const authRequest = localStorage.getItem(requestId + LocalStorageKey.authRequest)
        if (authRequest) {
          return JSON.parse(authRequest)
        }
        break
      case LocalStorageKey.serviceConfig:
        const serviceConfig = localStorage.getItem(requestId + LocalStorageKey.serviceConfig)
        if (serviceConfig) {
          return JSON.parse(serviceConfig)
        }
        break
      default:
        break
    }
  }

  return null
}

function getAuthorization() {
  if (checkToken()) {
    return auth.token_type + ' ' + auth.access_token
  }
  return null
}

function getRequest(endpoint: string, accept: string = 'application/json'): AxiosRequestConfig {
  return {
    url: BitBucketAPI.url + APIEndpoint.repositories + endpoint,
    method: 'GET',
    headers: {
      Accept: accept,
      Authorization: getAuthorization()
    }
  }
}

function getRequestURL(url: string, accept: string = 'application/json'): AxiosRequestConfig {
  return {
    url: url,
    method: 'GET',
    headers: {
      Accept: accept,
      Authorization: getAuthorization()
    }
  }
}

function postRequest(endpoint: string, content: any, contentType: string = 'application/json'): AxiosRequestConfig {
  return {
    url: BitBucketAPI.url + APIEndpoint.repositories + endpoint,
    method: 'POST',
    data: content,
    headers: {
      'Content-Type': contentType,
      Authorization: getAuthorization()
    }
  }
}

function getPartialResponse(fields: string[]): string {
  let toReturn = Query.prefix + PartialResponse.fields + PartialResponse.separator
  toReturn += fields.join(PartialResponse.separator)
  return toReturn
}

function getQueryString(queries: QueryObject[], concat: LogicalOperators[] = []): string {
  let toReturn = Query.search
  queries.forEach((query, index) => {
    if (concat[index]) {
      toReturn += convertToString(query) + ' ' + concat[index] + ' '
    } else {
      toReturn += convertToString(query)
    }
  })
  return toReturn
  function convertToString(query: QueryObject): string {
    let toReturn = ''
    if (typeof query.value === 'string') {
      toReturn = query.key + query.operator + '"' + query.value + '"'
    } else {
      toReturn = query.key + query.operator + query.value
    }
    return toReturn
  }
}

function pageLength(pagelen: number): string {
  return Query.pagelen + pagelen
}

function createResponse(response: AxiosResponse<any>): APIResponse {
  return {
    success: true,
    code: response.status,
    result: response.data
  }
}

function createErrorResponse(error: AxiosError<any>, request: any): APIResponse {
  if (error.response) {
    return {
      success: false,
      code: error.response.status,
      result: {
        message: error.message,
        request: request
      }
    }
  } else {
    return {
      success: false,
      code: 500,
      result: {
        message: 'Unknown Error'
      }
    }
  }
}

//api calls
export async function apiUser(): Promise<APIResponse> {
  const url = BitBucketAPI.url + APIEndpoint.user
  const userAPI = getRequestURL(url)
  try {
    const response = await axios.request(userAPI)
    return createResponse(response)
  } catch (error) {
    const request = {
      url: APIEndpoint.user
    }
    return createErrorResponse(error, request)
  }
}

export async function apiPipeline(repository: string): Promise<APIResponse> {
  const endpoint =
    repository +
    APIEndpoint.pipelines +
    getPartialResponse([PartialResponse.hashOfCommit, PartialResponse.stateOfPipeline]) +
    Query.and +
    pageLength(20) +
    Query.and +
    Query.sort.createdOn
  try {
    const apiPipelines = getRequest(endpoint)
    const response = await axios.request(apiPipelines)
    return createResponse(response)
  } catch (error) {
    const request = {
      url: endpoint,
      data: {
        repository
      }
    }
    return createErrorResponse(error, request)
  }
}

export async function apiPushFile(repository: string, data: FormData): Promise<APIResponse> {
  const endpoint = repository + APIEndpoint.src
  try {
    const postFile = postRequest(endpoint, data, 'multipart/form-data')
    const response = await axios.request(postFile)
    return createResponse(response)
  } catch (error) {
    const request = {
      url: endpoint,
      data: data
    }
    return createErrorResponse(error, request)
  }
}

export async function apiGetFile(repository: string, commit: string, file: string): Promise<APIResponse> {
  const endpoint = repository + APIEndpoint.src + commit + file
  try {
    const getFile = getRequest(endpoint, 'text/plain')
    const response = await axios.request(getFile)
    return createResponse(response)
  } catch (error) {
    const request = {
      url: endpoint,
      data: {
        repository: repository,
        commit: commit,
        file: file
      }
    }
    return createErrorResponse(error, request)
  }
}

export async function apiGetBranches(repository: string): Promise<APIResponse> {
  const endpoint =
    repository + APIEndpoint.head + getPartialResponse([PartialResponse.nameOfValues]) + Query.and + pageLength(100)
  try {
    const branchRequest = getRequest(endpoint)
    const response = await axios.request(branchRequest)
    return createResponse(response)
  } catch (error) {
    const request = {
      url: endpoint,
      data: {
        repository: repository
      }
    }
    return createErrorResponse(error, request)
  }
}

export async function apiGetHEADofBranch(repository: string, branch: string): Promise<APIResponse> {
  const endpoint = repository + APIEndpoint.head + branch + getPartialResponse([PartialResponse.hashOfTarget])
  try {
    const headRequest = getRequest(endpoint)
    const response = await axios.request(headRequest)
    return createResponse(response)
  } catch (error) {
    const request = {
      url: endpoint,
      data: {
        repository: repository,
        branch: branch
      }
    }
    return createErrorResponse(error, request)
  }
}

export async function apiGetBranchModel(repository: string): Promise<APIResponse> {
  const endpoint =
    repository +
    APIEndpoint.branchModel +
    getPartialResponse([PartialResponse.nameOfProduction, PartialResponse.nameOfDevelopment])
  try {
    const branchRequest = getRequest(endpoint)
    const response = await axios.request(branchRequest)
    return createResponse(response)
  } catch (error) {
    const request = {
      url: endpoint,
      data: {
        repository: repository
      }
    }
    return createErrorResponse(error, request)
  }
}

export async function apiGetLatestFeatureTags(repository: string, tag: string = Tag.feature): Promise<APIResponse> {
  const partialResponse = getPartialResponse([PartialResponse.nameOfValues, PartialResponse.targetHashOfValues])
  const featureTag: QueryObject = { key: 'name', operator: '~', value: tag }
  const query = getQueryString([featureTag])
  const endpoint =
    repository + APIEndpoint.tags + partialResponse + Query.and + query + Query.and + Query.sort.targetDate
  try {
    const tagRequest = getRequest(endpoint)
    const tagResponse = await axios.request(tagRequest)
    return createResponse(tagResponse)
  } catch (error) {
    const request = {
      url: endpoint,
      data: {
        repository: repository
      }
    }
    return createErrorResponse(error, request)
  }
}

export async function apiGetLatestMasterTags(repository: string) {
  const partialResponse = getPartialResponse([PartialResponse.nameOfValues, PartialResponse.targetHashOfValues])
  const masterReleaseTag: QueryObject = { key: 'name', operator: '~', value: 'release-2' }
  const notFeatureTag: QueryObject = { key: 'name', operator: '!~', value: Tag.feature }
  const notDevelopTag: QueryObject = { key: 'name', operator: '!~', value: Tag.develop }
  const query = getQueryString([masterReleaseTag, notFeatureTag, notDevelopTag], ['AND', 'AND'])
  const endpoint =
    repository + APIEndpoint.tags + partialResponse + Query.and + query + Query.and + Query.sort.targetDate
  try {
    const tagRequest = getRequest(endpoint)
    const tagResponse = await axios.request(tagRequest)
    return createResponse(tagResponse)
  } catch (error) {
    const request = {
      url: endpoint,
      data: {
        repository: repository
      }
    }
    return createErrorResponse(error, request)
  }
}

export async function apiGetLatestDevelopTags(repository: string) {
  const partialResponse = getPartialResponse([PartialResponse.nameOfValues, PartialResponse.targetHashOfValues])
  const developTag: QueryObject = { key: 'name', operator: '~', value: Tag.develop }
  const query = getQueryString([developTag])
  const endpoint =
    repository + APIEndpoint.tags + partialResponse + Query.and + query + Query.and + Query.sort.targetDate
  try {
    const tagRequest = getRequest(endpoint)
    const tagResponse = await axios.request(tagRequest)
    return createResponse(tagResponse)
  } catch (error) {
    const request = {
      url: endpoint,
      data: {
        repository: repository
      }
    }
    return createErrorResponse(error, request)
  }
}

export async function apiGetLatestTag(repository: string, tag: string): Promise<APIResponse> {
  const query = getQueryString([{ key: 'name', operator: '~', value: tag }])
  const partialResponse = getPartialResponse([PartialResponse.nameOfValues])
  const endpoint =
    repository +
    APIEndpoint.tags +
    partialResponse +
    Query.and +
    query +
    Query.and +
    pageLength(1) +
    Query.and +
    Query.sort.targetDate
  try {
    const getTagRequest = getRequest(endpoint)
    const response = await axios.request(getTagRequest)
    return createResponse(response)
  } catch (error) {
    const request = {
      url: endpoint,
      data: {
        repository: repository
      }
    }
    return createErrorResponse(error, request)
  }
}

export async function apiSetCommitTag(repository: string, tag: string, commit: string) {
  const endpoint = repository + APIEndpoint.tags
  try {
    const tagRequest = postRequest(endpoint, { name: tag, target: { hash: commit } })
    const response = await axios.request(tagRequest)
    return createResponse(response)
  } catch (error) {
    const request = {
      url: endpoint,
      data: {
        repository: repository,
        tag: tag,
        commit: commit
      }
    }
    return createErrorResponse(error, request)
  }
}

export async function apiCreatePullRequest(
  repository: string,
  sourceBranch: string,
  destinationBranch: string,
  title: string,
  close: boolean
) {
  const endpoint = repository + APIEndpoint.pullRequests + getPartialResponse([PartialResponse.idOfPullRequest])
  try {
    const content = {
      title: title,
      close_source_branch: close,
      source: {
        branch: {
          name: sourceBranch
        }
      },
      destination: {
        branch: {
          name: destinationBranch
        }
      }
    }
    const buildRequest = postRequest(endpoint, content)
    const response = await axios.request(buildRequest)
    return createResponse(response)
  } catch (error) {
    const request = {
      url: endpoint,
      data: {
        repository: repository,
        sourceBranch: sourceBranch,
        destinationBranch: destinationBranch
      }
    }
    if (error.response.data.error.message) {
      error.message = error.response.data.error.message
    }
    return createErrorResponse(error, request)
  }
}

export async function apiMergePullRequest(repository: string, pullRequest: number, message: string, close: boolean) {
  const endpoint =
    repository +
    APIEndpoint.pullRequests +
    pullRequest +
    APIEndpoint.merge +
    getPartialResponse([PartialResponse.hashOfMergeCommit])
  try {
    const content = {
      type: 'type',
      message: message,
      close_source_branch: close,
      merge_strategy: 'merge_commit'
    }
    const buildRequest = postRequest(endpoint, content)
    const response = await axios.request(buildRequest)
    return createResponse(response)
  } catch (error) {
    if (error.response.data.error.message) {
      error.message = error.response.data.error.message
    }
    const request = {
      url: endpoint,
      data: {
        repository: repository,
        pullRequest: pullRequest
      }
    }
    return createErrorResponse(error, request)
  }
}

export async function apiDiffStat(
  repository: string,
  sourceCommit: string,
  destinationCommit: string
): Promise<APIResponse> {
  //compares two commits
  const endpoint = repository + APIEndpoint.diff + sourceCommit + '..' + destinationCommit
  try {
    const diffRequest = getRequest(endpoint)
    const response = await axios.request(diffRequest)
    return createResponse(response)
  } catch (error) {
    const request = {
      url: endpoint,
      data: {
        repository: repository,
        commitDestination: destinationCommit,
        commitSource: sourceCommit
      }
    }
    return createErrorResponse(error, request)
  }
}

export async function apiStartPipeline(repository: string, branch: string, commit: string, selector: Selector) {
  const endpoint = repository + APIEndpoint.pipelines
  try {
    const content = {
      target: {
        commit: {
          hash: commit,
          type: 'commit'
        },
        selector: selector,
        type: 'pipeline_ref_target',
        ref_name: branch,
        ref_type: 'branch'
      }
    }
    const buildRequest = postRequest(endpoint, content)
    const response = await axios.request(buildRequest)
    return createResponse(response)
  } catch (error) {
    const request = {
      url: endpoint,
      data: {
        repository: repository,
        branch: branch,
        commit: commit
      }
    }
    return createErrorResponse(error, request)
  }
}

export async function codeRequest() {
  if (!(await checkToken())) {
    const authorizationHandler = new RedirectRequestHandler()
    const serviceConfig = new AuthorizationServiceConfiguration({
      authorization_endpoint: config.auth.authorization_endpoint,
      token_endpoint: config.auth.token_endpoint,
      revocation_endpoint: ''
    })
    const authRequest = new AuthorizationRequest({
      client_id: config.auth.client_id,
      redirect_uri: config.auth.redirect_uri,
      scope: config.auth.scopes,
      response_type: AuthorizationRequest.RESPONSE_TYPE_CODE,
      state: undefined,
      extras: { prompt: 'consent', access_type: 'offline' }
    })
    authorizationHandler.performAuthorizationRequest(serviceConfig, authRequest)
  }
}

export async function tokenRequest() {
  const serviceConfig = getLocalStorageItem(LocalStorageKey.serviceConfig)
  const authRequest = getLocalStorageItem(LocalStorageKey.authRequest)
  const urlParams = new URLSearchParams(window.location.search.slice(1))
  const code = urlParams.get('code')
  if (authRequest && serviceConfig && code) {
    if (urlParams.get('state') === authRequest.state) {
      const content: FormData = new FormData()
      content.append('client_id', config.auth.client_id)
      content.append('client_secret', config.auth.client_secret)
      content.append('redirect_uri', authRequest.redirect_uri)
      content.append('grant_type', GRANT_TYPE_AUTHORIZATION_CODE)
      content.append('code', code)
      if (authRequest.internal) {
        content.append('code_verifier', authRequest.internal['code_verifier'])
      }
      const accessTokenRequest = {
        url: serviceConfig.token_endpoint,
        method: 'POST',
        data: content,
        headers: {
          'content-type': 'application/x-www-form-urlencoded'
        }
      } as AxiosRequestConfig

      try {
        const response = await axios.request(accessTokenRequest)
        setAuthData(response.data)
        return createResponse(response)
      } catch (error) {
        return createErrorResponse(error, accessTokenRequest)
      }
    }
  }
}

export function checkToken() {
  if (auth) {
    if (auth.expires < Date.now()) {
      return false
    } else {
      return auth.expires - Date.now()
    }
  }
  return false
}

function clearStorage() {
  const requestId = localStorage.getItem(LocalStorageKey.requestId)
  if (requestId) {
    localStorage.removeItem(requestId + LocalStorageKey.serviceConfig)
    localStorage.removeItem(requestId + LocalStorageKey.authRequest)
    localStorage.removeItem(LocalStorageKey.requestId)
  }
}

export function setAuthData(data: any): void {
  auth = {
    valid: true,
    access_token: decodeURIComponent(data.access_token),
    scopes: data.scopes,
    //expires: Date.now() + Number(data.expire_in) * 1000,
    expires: Date.now() + 3600 * 1000, // documentation states that access_token expire in 1h but returns data.expire_in:7200 (=2h)
    token_type: data.token_type.charAt(0).toUpperCase() + data.token_type.slice(1)
  } as AuthenticationData
  clearStorage()
}
